import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Switch,
  message,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import BankForm from "./BankForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import moment from "moment-timezone";
import { deletePackage } from "../../store/actions/package.js";
import Swal from "sweetalert2";
import {
  addBank,
  deleteBank,
  getBank,
  updateBank,
  updateBankStatus,
} from "../../store/actions/bank.js";

const { Title } = Typography;

const Bank = () => {
  document.title = "Bank";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();
  const Bank = useSelector((state) => state.Bank.list);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await dispatch(getBank());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  const handleCreate = async (values) => {
    try {
      const formData = new FormData();
      formData.append("name", values.name);

      if (values.file && values.file.length > 0) {
        // Only append logo if it's a new file (has originFileObj)
        const fileObj = values.file[0].originFileObj;
        if (fileObj) {
          formData.append("file", fileObj);
        }
      }

      if (selectedRecord) {
        formData.append("id", selectedRecord.id);
        await dispatch(updateBank(formData));
      } else {
        await dispatch(addBank(formData));
      }
      await dispatch(getBank());
      setIsModalVisible(false);
    } catch (error) {
      console.error("Failed to add/edit bank", error);
    }
  };

  // const handleDelete = async (id) => {
  //   await dispatch(deleteBank(id));
  //   await dispatch(getBank());
  // };

  const handleDelete = async (item) => {
    Swal.fire({
      title: "Cannot delete active Bank",
      text: "Please deactivate the bank before trying to delete it.",
      icon: "warning",
    });
  };

  const handleConfirmDelete = async (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to remove bank: ${item.name}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await dispatch(deleteBank(item.id));
          await dispatch(getBank());
        } catch (error) {
          message.error("An error occurred while deleting the item");
        }
      }
    });
  };

  const handleChangeStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1; // Toggle status
    await dispatch(updateBankStatus({ id, status: newStatus }));
    await dispatch(getBank());
  };

  const renderCell = (text) => (text ? text : "N/A");
  let columns = [
    {
      title: "Logo",
      dataIndex: "logo",
      key: "logo",
      sorter: stringSorter("logo"),
      sortDirections: ["ascend", "descend"],
      render: (imagepath, record) => (
        <>
          <img
            src={record?.imagepath}
            alt={record.name || "Bank logo"}
            style={{ maxWidth: "100px", maxHeight: "100px" }}
            crossOrigin="anonymous"
          />
        </>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: stringSorter("name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD HH:mm:ss")
          : "",
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Switch
          checked={text === 1}
          onChange={() => handleChangeStatus(record.id, text)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => {
              setSelectedRecord(record);
              setIsModalVisible(true);
            }}
          >
            <EditOutlined />
          </Button>
          {record?.status ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              {/* Delete */}
            </Button>
          ) : (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleConfirmDelete(record)}
            >
              {/* Delete */}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
       <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Bank</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2}>Manage Bank</Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Bank
              </Button>
            </Col>
          </Row>

          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DataTable
              dataSource={Bank.map((item) => ({
                ...item,
                key: item.id,
                status: item.status ? 1 : 0,
              }))}
              columns={columns}
            />
          )}
        </Space>
      </Card>

      <BankForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => setIsModalVisible(false)}
        record={selectedRecord}
      />
    </div>
  );
};

export default Bank;
